import { useMemo } from 'react'
import { CountryCode } from '@carsys/enums/country-code'

/**
 * Hook that extracts the country code directly from the current URL
 * Useful for onboarding flows where no shop context is available yet
 *
 * Examples:
 * - https://nl.carsys.online/ → 'nl'
 * - https://rc.be.carsys.online/ → 'be'
 * - https://develop.nl.carsys.online/ → 'nl'
 *
 * @returns Object with country code and helper booleans
 */
export const useCountryFromUrl = () => {
  const country = useMemo(() => {
    try {
      const host = window.location.host
      const hostParts = host.split('.')

      // Handle different URL patterns:
      // develop.nl.carsys.online (4 parts) - country is at index 1
      // rc.nl.carsys.online (4 parts) - country is at index 1
      // staging.be.carsys.online (4 parts) - country is at index 1
      // nl.carsys.online (3 parts) - country is at index 0

      if (hostParts.length >= 4) {
        // Environment prefix exists, country is second part
        return hostParts[1].toLowerCase()
      } else if (hostParts.length === 3) {
        // No environment prefix, country is first part
        return hostParts[0].toLowerCase()
      }

      // Fallback for localhost or other formats - default to Netherlands
      return 'nl'
    } catch (error) {
      console.warn('Failed to extract country from URL:', error)
      return 'nl'
    }
  }, [])

  return {
    country,
    isNetherlands: country === 'nl',
    isBelgium: country === 'be',
    isGermany: country === 'de',
    isFrance: country === 'fr',
    isUnitedKingdom: country === 'gb' || country === 'uk',
    countryCode: country.toUpperCase() as CountryCode
  }
}
